import React from 'react'
import { useEffect, useRef, useState, useCallback } from 'react'
import { FOCUS_RING } from '../utils/a11y'
import { X } from 'lucide-react'
import imgServiceDevelopment from '../../assets/services/service-development-blured.webp'
import imgServiceDesign from '../../assets/services/service-design-blured.webp'
import imgServiceManagement from '../../assets/services/service-management-blured.webp'
import imgServiceOutsourcing from '../../assets/services/service-outsourcing-blured.webp'
import imgServiceRework from '../../assets/services/service-rework-blured.webp'
import imgServiceConsult from '../../assets/services/service-consult-blured.webp'
import icArrowRight1 from '../../assets/icon/ic_arrow_right1.svg'
import icArrowRight2 from '../../assets/icon/ic_arrow_right2.svg'

const SERVICES = [
    {
        title: "Development",
        image: imgServiceDevelopment,
        subTitles: [
            "Mobile apps — native & cross-platform",
            "Web apps & responsive frontends",
            "Backend, APIs & databases",
            "DevOps, CI/CD & cloud infrastructure",
            "Quality audits & technical consulting",
            "Manual Quality Assurance Testing",
        ],
    },
    {
        title: "Design & Vision",
        image: imgServiceDesign,
        subTitles: [
            "User Experience Assessment & Enhancements",
            "UI/UX Design",
            "Development of Component Library & Style Guide",
            "Branding",
        ],
    },
    {
        title: "Operation",
        image: imgServiceManagement,
        subTitles: ["Project Management", "Resource management", "Delivery Management"],
    },
    {
        title: "Outsource",
        image: imgServiceOutsourcing,
        subTitles: [
            "Mobile Development",
            "Web Development",
            "Project Management",
            "Quality Assurance & Testing",
            "UI/UX Design",
            "Graphic Design",
        ],
    },
    {
        title: "Rework",
        image: imgServiceRework,
        subTitles: [
            "Re-launch of Existing Products",
            "Reorganization of Underperforming Teams for Improved Efficiency",
            "Ongoing Project Enhancements",
            "Development of Customized Solutions for Optimized Release Processes",
        ],
    },
    {
        title: "Consultation",
        image: imgServiceConsult,
        subTitles: [
            "Product & Platform Strategy",
            "Technology Stack & Architecture",
            "Monetization Strategy",
            "Performance Analysis",
            "User Acquisition & Retention",
            "Compliance & Scalability",
        ],
    },
]

const COUNT = SERVICES.length
const CLOSE_MS = 320
const FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'

/* Next-service control. direction='right' is the mobile look (label + rightward
   chevrons in a row); 'down' is the desktop variant — label over two DOWN
   chevrons. The rotation is a static class on the img and the animation runs on
   the UNROTATED wrapper, because translateY inside a rotated element renders as
   visual LEFT drift (and the reduced-motion collapse would strip a rotation
   living in the keyframes). */
function StepButton({ className, title, onActivate, direction = 'down', label }) {
    const isDown = direction === 'down'
    return (
        <button
            type="button"
            aria-label={label}
            className={`${className} ${isDown ? 'flex-col' : ''} items-center cursor-pointer ${FOCUS_RING} rounded-2xl px-3 py-2`}
            onClick={onActivate}>
            <span className="text-[#ABA8A1] text-14 nav-sm:text-16">{title}</span>
            {isDown ? (
                <span className="flex flex-col items-center mt-1">
                    <span className="block arrow-next-down-1"><img src={icArrowRight1} alt="" width={24} height={24} className="w-5 h-5 rotate-90" loading="lazy" /></span>
                    <span className="block arrow-next-down-2 -mt-3"><img src={icArrowRight2} alt="" width={24} height={24} className="w-5 h-5 rotate-90" loading="lazy" /></span>
                </span>
            ) : (
                <span className="flex items-center">
                    <img src={icArrowRight1} alt="" width={24} height={24} className="w-5 h-5 arrow-next-1 ml-2" loading="lazy" />
                    <img src={icArrowRight2} alt="" width={24} height={24} className="w-5 h-5 arrow-next-2 mr-2" loading="lazy" />
                </span>
            )}
        </button>
    )
}

export default function ServicePopup({
    isServicesPopupVisible,
    toggleServicesPopup,
    selectedServiceOrder,
    updateSelectedServiceOrder,
    popupStyle,
}) {
    const [isClosing, setIsClosing] = useState(false)
    const [animationKey, setAnimationKey] = useState(0)
    const panelRef = useRef(null)
    const closeRef = useRef(null)
    const closeTimer = useRef(null)
    const restoreFocusTo = useRef(null)

    const service = SERVICES[selectedServiceOrder] || SERVICES[0]

    /* Closing is a single interruptible pass. It used to setState, wait out a
     * fixed 500ms setTimeout and only then actually close — so a second click
     * during that window did nothing at all. */
    const handleClose = useCallback(() => {
        if (closeTimer.current) return
        setIsClosing(true)
        closeTimer.current = setTimeout(() => {
            closeTimer.current = null
            setIsClosing(false)
            toggleServicesPopup(null, 0)
        }, CLOSE_MS)
    }, [toggleServicesPopup])

    const goTo = useCallback(
        (index) => {
            updateSelectedServiceOrder(((index % COUNT) + COUNT) % COUNT)
            setAnimationKey((k) => k + 1)
        },
        [updateSelectedServiceOrder]
    )
    const goNext = useCallback(() => goTo(selectedServiceOrder + 1), [goTo, selectedServiceOrder])
    const goPrev = useCallback(() => goTo(selectedServiceOrder - 1), [goTo, selectedServiceOrder])

    /* Open: remember where focus came from, move it into the panel. Close:
     * hand it back. Neither happened before — this was a full-screen overlay
     * with no dialog semantics at all. */
    useEffect(() => {
        if (!isServicesPopupVisible) return undefined
        restoreFocusTo.current = document.activeElement
        setIsClosing(false)
        const id = requestAnimationFrame(() => {
            if (closeRef.current) closeRef.current.focus()
        })
        return () => {
            cancelAnimationFrame(id)
            const el = restoreFocusTo.current
            if (el && typeof el.focus === 'function') el.focus()
        }
    }, [isServicesPopupVisible])

    useEffect(() => () => clearTimeout(closeTimer.current), [])

    useEffect(() => {
        if (!isServicesPopupVisible) return undefined
        const onKeyDown = (e) => {
            if (e.key === 'Escape') {
                e.stopPropagation()
                handleClose()
                return
            }
            if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
                e.preventDefault()
                goNext()
                return
            }
            if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
                e.preventDefault()
                goPrev()
                return
            }
            if (e.key !== 'Tab' || !panelRef.current) return
            const items = panelRef.current.querySelectorAll(FOCUSABLE)
            if (!items.length) return
            const first = items[0]
            const last = items[items.length - 1]
            if (e.shiftKey && document.activeElement === first) {
                e.preventDefault()
                last.focus()
            } else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault()
                first.focus()
            }
        }
        document.addEventListener('keydown', onKeyDown, true)
        return () => document.removeEventListener('keydown', onKeyDown, true)
    }, [isServicesPopupVisible, handleClose, goNext, goPrev])

    /* One wheel gesture = one page, with a cooldown that swallows a trackpad's
     * momentum burst and a deltaY floor that ignores micro-scrolls. */
    const lastPaginateAt = useRef(0)
    const onWheel = (e) => {
        if (!window.matchMedia('(min-width: 860px)').matches) return
        if (Math.abs(e.deltaY) < 20) return
        const now = Date.now()
        if (now - lastPaginateAt.current < 700) return
        lastPaginateAt.current = now
        if (e.deltaY > 0) goNext()
        else goPrev()
    }

    /* Touch: the popup had no back at all on a phone. */
    const touch = useRef(null)
    const onTouchStart = (e) => {
        touch.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }
    }
    const onTouchEnd = (e) => {
        const t = touch.current
        touch.current = null
        if (!t) return
        const dx = e.changedTouches[0].clientX - t.x
        const dy = e.changedTouches[0].clientY - t.y
        if (Math.abs(dx) < 48 || Math.abs(dx) < Math.abs(dy)) return
        if (dx < 0) goNext()
        else goPrev()
    }

    if (!isServicesPopupVisible && !isClosing) return null

    return (
        <div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-label={`${service.title} — services`}
            className={`popup-surface ${isClosing ? 'popup-closing' : 'popup-grow-content'} fixed inset-0 z-[1005] flex items-center justify-center w-full bg-black no-select`}
            style={popupStyle}
            onWheel={onWheel}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}>
            <div className="relative w-full h-full">
                <img src={service.image} alt="" width={512} height={483} className="absolute inset-0 w-full h-full object-cover z-0 scale-[1.05]" loading="lazy" />
                <div className="absolute inset-0 w-full h-full bg-[#1F1F1F] opacity-[0.72]" />
                <div className="w-full h-full flex">
                    {/* The rail was six <div>s — a progress display you could
                        not operate. They are buttons now, so every service is
                        reachable directly and backwards, not only via Next. */}
                    <div className="hidden nav-sm:flex w-20 items-center justify-center ml-16 z-10">
                        <div className="relative flex flex-col items-center h-2/3">
                            <div className="w-1 h-full absolute py-2">
                                <div className="w-1 h-full bg-[#8B8B8B] opacity-50 rounded-lg" />
                            </div>
                            <div className="flex flex-col justify-between h-full z-10 items-center">
                                {SERVICES.map((s, index) => (
                                    <button
                                        key={s.title}
                                        type="button"
                                        onClick={() => goTo(index)}
                                        aria-label={`Show ${s.title}`}
                                        aria-current={index === selectedServiceOrder}
                                        className={`grid place-items-center w-[44px] h-[44px] rounded-full ${FOCUS_RING}`}>
                                        <span
                                            className={`block transition-[width,height,background-color,border-color] duration-500 ease-in-out rounded-full ${
                                                index === selectedServiceOrder
                                                    ? 'w-5 h-5 bg-colorPrimary'
                                                    : 'w-3 h-3 border-2 border-[#E4E2DD]'
                                            }`}
                                        />
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="flex-grow w-full z-10">
                        <div className="flex flex-col h-full mx-[32px] c-md:mx-[62px] nav-sm:ml-[130px] c-lg:ml-[200px] c-xl:ml-[200px] nav-sm:justify-center mt-[96px] nav-sm:mt-0">
                            <div className="w-full">
                                {/* Content renders immediately. It used to sit
                                    behind a hard 500ms setTimeout — half a
                                    second of a blank panel on every open — and
                                    the title's only visible state came from a
                                    hand-mutated classList over an inline
                                    `opacity: isLoaded ? 0.01 : 0.01`, whose two
                                    branches were identical. */}
                                <h2
                                    key={`title-${animationKey}`}
                                    className="text-48 c-md:text-56 c-2lg:text-90 text-white font-bold animated-service-paragraph my-[12px] c-md:my-[24px]">
                                    {service.title}
                                </h2>

                                <ul className="min-h-[259px] nav-sm:min-h-[278px] c-2lg:min-h-[352px] w-full">
                                    {service.subTitles.map((item, index) => (
                                        <li
                                            key={`${animationKey}-${item}`}
                                            className="service-popup-p my-[14px] nav-sm:my-[20px] text-18 c-md:text-20 c-2lg:text-28 text-[#E4E2DD] animated-service-paragraph"
                                            style={{ animationDelay: `${(index + 1) * 0.05}s`, opacity: 0 }}>
                                            {item}
                                        </li>
                                    ))}
                                </ul>

                                <div className="w-full flex justify-center nav-sm:justify-start">
                                    <a
                                        href={`mailto:info@gravisun.com?subject=${encodeURIComponent(service.title)}`}
                                        className={`min-h-[57px] mt-[44px] text-[#E4E2DD] text-18 nav-sm:text-24 service-popup-p bg-[#18101057] backdrop-blur-md border border-white/15 py-[15px] nav-sm:py-[20px] px-[39px] nav-sm:px-[54px] rounded-full inline-flex items-center transition-[border-color,transform] duration-200 ease-out hover:border-white/30 hover:-translate-y-[2px] active:translate-y-0 ${FOCUS_RING}`}>
                                        Contact us
                                    </a>
                                </div>
                            </div>
                        </div>

                        <StepButton
                            direction="right"
                            label={`Next service: ${SERVICES[(selectedServiceOrder + 1) % COUNT].title}`}
                            className="flex nav-sm:hidden absolute bottom-0 ml-[32px] c-md:ml-[62px] service-next-mb service-description-next-pb"
                            title={SERVICES[(selectedServiceOrder + 1) % COUNT].title}
                            onActivate={goNext}
                        />
                        <StepButton
                            direction="down"
                            label={`Next service: ${SERVICES[(selectedServiceOrder + 1) % COUNT].title}`}
                            className="hidden nav-sm:flex absolute bottom-[36px] left-1/2 -translate-x-1/2 z-20"
                            title={SERVICES[(selectedServiceOrder + 1) % COUNT].title}
                            onActivate={goNext}
                        />
                    </div>
                </div>

                {/* One close control, always in the same place, always saying
                    the same thing. The old pair were <div role="button">s whose
                    label swapped to an X glyph on hover — and they shared one
                    `isHovered` state, so hovering either changed both. */}
                <button
                    ref={closeRef}
                    type="button"
                    onClick={handleClose}
                    aria-label="Close services"
                    className={`group absolute top-[32px] right-[32px] c-md:right-[62px] nav-sm:top-[48px] nav-sm:right-[65px] z-20 inline-flex items-center gap-2 h-[44px] px-[14px] rounded-full text-[#ABA8A1] hover:text-white hover:bg-white/10 transition-colors duration-200 ${FOCUS_RING}`}>
                    <span className="text-14 nav-sm:text-16">Close</span>
                    <X className="size-[20px]" />
                </button>
            </div>
        </div>
    )
}
